import { Injectable } from '@angular/core';
import { State } from '@app/store';
import {
  allInventoriesView,
  selectGlCodes,
  selectInventoriesForCostReportModal,
} from '@inventory/selectors/inventory-view-model.selector';
import { Store } from '@ngrx/store';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import {
  ANALYTICS_ACTIONS,
  InventoriesSortOptions,
  INVENTORY_ACTIONS,
  InventoryAnalyticsConstants,
  pageLoadedTransformer,
  selectInventoryOptions,
  SortDirections,
} from '@usf/ngrx-inventory';
import { Observable, combineLatest, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AllInventoryService {
  constructor(
    private store: Store<State>,
    private panAppState: PanAppState,
  ) {}

  // OBSERVABLE SELECTORS
  allInventoriesView$ = (platform: PlatformEnum) => {
    return combineLatest([
      this.store.select(allInventoriesView(platform)),
      this.selectCostReportFlag$(),
      this.selectInventoriesForCostReport$(),
      this.selectShowGl$(),
    ]).pipe(
      map(
        ([
          inventoryPageData,
          costReportModalFlag,
          costReportInventories,
          glFlag,
        ]) => {
          return {
            ...inventoryPageData,
            costReportModalFlag,
            costReportInventories,
            glFlag,
          };
        },
      ),
    );
  };

  selectInventoryOptions$ = () => this.store.select(selectInventoryOptions());

  // INVENTORY ACTIONS
  updateAllInventoriesSort(selectedHeader: string, sortDirection: string) {
    this.store.dispatch(
      INVENTORY_ACTIONS.updateAllInventoriesSort({
        selectedHeader,
        sortDirection,
      }),
    );
  }

  searchInAllInventories(searchKey: string) {
    this.store.dispatch(
      INVENTORY_ACTIONS.searchInAllInventories({ searchKey }),
    );
  }

  resetAllInventoriesSort() {
    this.store.dispatch(INVENTORY_ACTIONS.resetAllInventoriesSort());
  }

  toggleShowCompleted(showCompleted: boolean) {
    this.store.dispatch(
      INVENTORY_ACTIONS.toggleShowCompleted({ showCompleted }),
    );
    showCompleted
      ? this.updateAllInventoriesSort('completedDtm', SortDirections.desc)
      : this.updateAllInventoriesSort(
          InventoriesSortOptions.created,
          SortDirections.desc,
        );
  }

  selectCostReportFlag$ = (): Observable<boolean> => {
    return this.panAppState.feature$([
      FEATURES.split_global_inventory_cost_report.name,
    ]);
  };

  selectInventoriesForCostReport$() {
    return this.store.select(selectInventoriesForCostReportModal());
  }

  selectShowGl$(): Observable<boolean> {
    return combineLatest([
      this.store.select(selectGlCodes()),
      this.panAppState.feature$([
        FEATURES.split_global_inventory_gl_sorting.name,
      ]),
    ]).pipe(
      map(
        ([generalLedgerArray, glFeatureFlag]) =>
          generalLedgerArray?.length > 0 && glFeatureFlag,
      ),
    );
  }

  trackAllInventoriesPageLoaded = () => {
    this.store.dispatch(
      ANALYTICS_ACTIONS.trackAllInventoriesPageLoaded({
        tracking: {
          analytics: {
            data: {
              capability: InventoryAnalyticsConstants.capability,
              event: InventoryAnalyticsConstants.pageLoaded,
              pageName: InventoryAnalyticsConstants.allInventoriesPage,
            },
            transformFunc: pageLoadedTransformer,
          },
        },
      }),
    );
  };
}
